import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { portfolioProductsService, portfolioServicesService } from '../../../services/portfolioService';

const PortfolioComparisonPanel = ({ partners = [], onClose }) => {
  const [partnerAId, setPartnerAId] = useState('');
  const [partnerBId, setPartnerBId] = useState('');
  const [dataA, setDataA] = useState({ products: [], services: [] });
  const [dataB, setDataB] = useState({ products: [], services: [] });
  const [loading, setLoading] = useState(false);
  const [compared, setCompared] = useState(false);

  const partnerA = partners?.find(p => p?.id === partnerAId);
  const partnerB = partners?.find(p => p?.id === partnerBId);

  const handleCompare = async () => {
    if (!partnerAId || !partnerBId) return;
    setLoading(true);
    try {
      const [prodsA, svcsA, prodsB, svcsB] = await Promise.all([
        portfolioProductsService?.getAll(partnerAId),
        portfolioServicesService?.getAll(partnerAId),
        portfolioProductsService?.getAll(partnerBId),
        portfolioServicesService?.getAll(partnerBId),
      ]);
      setDataA({ products: prodsA || [], services: svcsA || [] });
      setDataB({ products: prodsB || [], services: svcsB || [] });
      setCompared(true);
    } catch (err) {
      console.error('Comparison error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSharedProducts = () => {
    const namesA = new Set(dataA?.products?.map(p => p?.productName?.toLowerCase()));
    return dataB?.products?.filter(p => namesA?.has(p?.productName?.toLowerCase()));
  };

  const getUniqueToA = () => {
    const namesB = new Set(dataB?.products?.map(p => p?.productName?.toLowerCase()));
    return dataA?.products?.filter(p => !namesB?.has(p?.productName?.toLowerCase()));
  };

  const getUniqueToB = () => {
    const namesA = new Set(dataA?.products?.map(p => p?.productName?.toLowerCase()));
    return dataB?.products?.filter(p => !namesA?.has(p?.productName?.toLowerCase()));
  };

  const getSharedServices = () => {
    const namesA = new Set(dataA?.services?.map(s => s?.serviceName?.toLowerCase()));
    return dataB?.services?.filter(s => namesA?.has(s?.serviceName?.toLowerCase()));
  };

  const partnerOptions = partners?.filter(p => p?.id !== partnerBId);
  const partnerBOptions = partners?.filter(p => p?.id !== partnerAId);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-20 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center gap-12">
          <div className="p-10 bg-white rounded-xl shadow-sm">
            <Icon name="GitCompare" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Portfolio Comparison</h3>
            <p className="text-sm text-slate-500">Compare portfolios between two partners</p>
          </div>
        </div>
        <button onClick={onClose} className="p-8 hover:bg-white/80 rounded-lg transition-colors">
          <Icon name="X" size={18} className="text-slate-500" />
        </button>
      </div>
      {/* Partner Selection */}
      <div className="p-20 border-b border-slate-100">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-16 items-center">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-8 uppercase tracking-wide">Partner A</label>
            <select
              value={partnerAId}
              onChange={(e) => { setPartnerAId(e?.target?.value); setCompared(false); }}
              className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
            >
              <option value="">Select partner...</option>
              {partnerOptions?.map(p => (
                <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-center">
            <div className="p-10 bg-slate-100 rounded-full">
              <Icon name="ArrowLeftRight" size={18} className="text-slate-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-8 uppercase tracking-wide">Partner B</label>
            <select
              value={partnerBId}
              onChange={(e) => { setPartnerBId(e?.target?.value); setCompared(false); }}
              className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
            >
              <option value="">Select partner...</option>
              {partnerBOptions?.map(p => (
                <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-16 flex justify-center">
          <Button
            variant="default"
            onClick={handleCompare}
            disabled={!partnerAId || !partnerBId || loading}
            iconName="GitCompare"
            iconPosition="left"
            iconSize={16}
          >
            {loading ? 'Comparing...' : 'Compare Portfolios'}
          </Button>
        </div>
      </div>
      {/* Comparison Results */}
      {compared && !loading && (
        <div className="p-20 space-y-20">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            <div className="text-center p-14 bg-blue-50 rounded-xl border border-blue-100">
              <div className="text-2xl font-bold text-blue-700">{dataA?.products?.length}</div>
              <div className="text-xs text-blue-500 mt-2">{partnerA?.brand_name || partnerA?.legal_name} Products</div>
            </div>
            <div className="text-center p-14 bg-purple-50 rounded-xl border border-purple-100">
              <div className="text-2xl font-bold text-purple-700">{dataB?.products?.length}</div>
              <div className="text-xs text-purple-500 mt-2">{partnerB?.brand_name || partnerB?.legal_name} Products</div>
            </div>
            <div className="text-center p-14 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="text-2xl font-bold text-emerald-700">{getSharedProducts()?.length}</div>
              <div className="text-xs text-emerald-500 mt-2">Shared Products</div>
            </div>
            <div className="text-center p-14 bg-amber-50 rounded-xl border border-amber-100">
              <div className="text-2xl font-bold text-amber-700">{getSharedServices()?.length}</div>
              <div className="text-xs text-amber-500 mt-2">Shared Services</div>
            </div>
          </div>

          {/* Products Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Unique to A */}
            <div className="bg-blue-50 rounded-xl p-16 border border-blue-100">
              <div className="flex items-center gap-8 mb-12">
                <div className="w-8 h-8 rounded-full bg-blue-500"></div>
                <span className="text-sm font-semibold text-blue-800">
                  Only {partnerA?.brand_name || partnerA?.legal_name}
                </span>
                <span className="ml-auto text-xs font-bold text-blue-600 bg-blue-100 px-8 py-3 rounded-full">
                  {getUniqueToA()?.length}
                </span>
              </div>
              <div className="space-y-6 max-h-[200px] overflow-y-auto">
                {getUniqueToA()?.length === 0 ? (
                  <p className="text-xs text-blue-500 italic">No unique products</p>
                ) : (
                  getUniqueToA()?.map(p => (
                    <div key={p?.id} className="flex items-center gap-6 text-xs text-blue-700">
                      <Icon name="Package" size={12} className="flex-shrink-0" />
                      <span className="truncate">{p?.productName}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Shared */}
            <div className="bg-emerald-50 rounded-xl p-16 border border-emerald-100">
              <div className="flex items-center gap-8 mb-12">
                <div className="w-8 h-8 rounded-full bg-emerald-500"></div>
                <span className="text-sm font-semibold text-emerald-800">Shared Products</span>
                <span className="ml-auto text-xs font-bold text-emerald-600 bg-emerald-100 px-8 py-3 rounded-full">
                  {getSharedProducts()?.length}
                </span>
              </div>
              <div className="space-y-6 max-h-[200px] overflow-y-auto">
                {getSharedProducts()?.length === 0 ? (
                  <p className="text-xs text-emerald-500 italic">No shared products</p>
                ) : (
                  getSharedProducts()?.map(p => (
                    <div key={p?.id} className="flex items-center gap-6 text-xs text-emerald-700">
                      <Icon name="CheckCircle2" size={12} className="flex-shrink-0" />
                      <span className="truncate">{p?.productName}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Unique to B */}
            <div className="bg-purple-50 rounded-xl p-16 border border-purple-100">
              <div className="flex items-center gap-8 mb-12">
                <div className="w-8 h-8 rounded-full bg-purple-500"></div>
                <span className="text-sm font-semibold text-purple-800">
                  Only {partnerB?.brand_name || partnerB?.legal_name}
                </span>
                <span className="ml-auto text-xs font-bold text-purple-600 bg-purple-100 px-8 py-3 rounded-full">
                  {getUniqueToB()?.length}
                </span>
              </div>
              <div className="space-y-6 max-h-[200px] overflow-y-auto">
                {getUniqueToB()?.length === 0 ? (
                  <p className="text-xs text-purple-500 italic">No unique products</p>
                ) : (
                  getUniqueToB()?.map(p => (
                    <div key={p?.id} className="flex items-center gap-6 text-xs text-purple-700">
                      <Icon name="Package" size={12} className="flex-shrink-0" />
                      <span className="truncate">{p?.productName}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Services Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="bg-slate-50 rounded-xl p-16 border border-slate-200">
              <div className="flex items-center gap-8 mb-12">
                <Icon name="Wrench" size={14} className="text-slate-600" />
                <span className="text-sm font-semibold text-slate-700">{partnerA?.brand_name || partnerA?.legal_name} Services</span>
                <span className="ml-auto text-xs font-bold text-slate-600 bg-slate-200 px-8 py-3 rounded-full">{dataA?.services?.length}</span>
              </div>
              <div className="space-y-6 max-h-[160px] overflow-y-auto">
                {dataA?.services?.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No services mapped</p>
                ) : (
                  dataA?.services?.map(s => (
                    <div key={s?.id} className="flex items-center gap-6 text-xs text-slate-600">
                      <Icon name="Wrench" size={11} className="flex-shrink-0" />
                      <span className="truncate">{s?.serviceName}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-16 border border-slate-200">
              <div className="flex items-center gap-8 mb-12">
                <Icon name="Wrench" size={14} className="text-slate-600" />
                <span className="text-sm font-semibold text-slate-700">{partnerB?.brand_name || partnerB?.legal_name} Services</span>
                <span className="ml-auto text-xs font-bold text-slate-600 bg-slate-200 px-8 py-3 rounded-full">{dataB?.services?.length}</span>
              </div>
              <div className="space-y-6 max-h-[160px] overflow-y-auto">
                {dataB?.services?.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No services mapped</p>
                ) : (
                  dataB?.services?.map(s => (
                    <div key={s?.id} className="flex items-center gap-6 text-xs text-slate-600">
                      <Icon name="Wrench" size={11} className="flex-shrink-0" />
                      <span className="truncate">{s?.serviceName}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {!compared && !loading && (
        <div className="flex flex-col items-center justify-center py-48 text-center px-24">
          <div className="p-20 bg-slate-100 rounded-full mb-16">
            <Icon name="GitCompare" size={32} className="text-slate-400" />
          </div>
          <p className="text-slate-600 font-medium">Select two partners and click Compare</p>
          <p className="text-slate-400 text-sm mt-4">See shared products, unique items, and service overlap</p>
        </div>
      )}
    </div>
  );
};

export default PortfolioComparisonPanel;
